// Canvas — right column of the studio shell. Renders an iframe preview
// of the active project's "current" HTML file.
//
// Preview-file selection (heuristic, applied on every refresh):
//   1. index.html at project root
//   2. deck.html at project root
//   3. Alphabetically-first .html file at project root
//   4. None — show empty state
//
// The single-file expectation matches the system prompt's guidance
// (agent inlines CSS via <style> + assets via data URIs). Multi-file
// projects with relative <link>/<img>/<script> references won't render
// correctly here yet — srcDoc has no base URL and the iframe sandbox
// blocks cross-origin loads. Phase 3+ may add a Service Worker or Blob
// URL graph to fix that; for first usable cut, single-file decks cover
// 95% of what the agent produces.
//
// Refresh triggers:
//   - projectName change (re-bind)
//   - useFsRevision().revision bump (orchestrator signals a write)
//
// Sandbox: allow-scripts only. No same-origin (defense against generated
// HTML reaching back into our origin).

import { useEffect, useState } from 'react';

import { defaultFileStore } from '../core/filestore';
import { useFsRevision } from '../state/fs-revision';

const PREFERRED_NAMES = ['index.html', 'deck.html'];

export interface CanvasProps {
  projectName: string;
}

export function Canvas({ projectName }: CanvasProps) {
  const revision = useFsRevision((s) => s.revision);
  const [previewFile, setPreviewFile] = useState<string | null>(null);
  const [previewContent, setPreviewContent] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const fileName = await pickPreviewFile(projectName);
        if (cancelled) return;
        if (!fileName) {
          setPreviewFile(null);
          setPreviewContent('');
          return;
        }
        const content = await defaultFileStore.readTextFile(
          `${projectName}/${fileName}`,
        );
        if (cancelled) return;
        setPreviewFile(fileName);
        setPreviewContent(content);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [projectName, revision]);

  if (error) {
    return (
      <div className="flex h-full items-center justify-center bg-[#08080c] px-6 text-center">
        <div>
          <p className="text-[12px] text-[#ff8a8a]">Canvas error</p>
          <p className="mt-2 text-[11px] text-neutral-600">{error}</p>
        </div>
      </div>
    );
  }

  if (!previewFile) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-[#08080c] px-6 text-center">
        <p className="text-[11px] uppercase tracking-[0.18em] text-neutral-700">
          Canvas
        </p>
        <p className="mt-3 text-[12px] text-neutral-500">
          仲未有 HTML file
        </p>
        <p className="mt-1 text-[11px] text-neutral-600">
          叫 agent 寫 <code className="font-mono text-neutral-400">index.html</code>{' '}
          或者 <code className="font-mono text-neutral-400">deck.html</code> 就會
          render 喺度。
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-[#08080c]">
      <div className="flex shrink-0 items-center justify-between border-b border-[#222230] px-3 py-1.5">
        <span className="font-mono text-[10px] text-neutral-500">
          {previewFile}
        </span>
        <span className="text-[10px] text-neutral-700">
          {loading ? '更新中…' : `r${revision}`}
        </span>
      </div>
      <iframe
        // key forces React to recreate the iframe element on each
        // revision rather than diffing srcDoc — which would let stale
        // scripts persist between renders.
        key={`${previewFile}@${revision}`}
        title={`preview of ${previewFile}`}
        srcDoc={previewContent}
        sandbox="allow-scripts"
        className="flex-1 bg-white"
      />
    </div>
  );
}

async function pickPreviewFile(projectName: string): Promise<string | null> {
  let entries;
  try {
    entries = await defaultFileStore.listDir(projectName);
  } catch {
    return null;
  }
  const fileNames = entries.filter((e) => e.kind === 'file').map((e) => e.name);
  for (const preferred of PREFERRED_NAMES) {
    if (fileNames.includes(preferred)) return preferred;
  }
  const htmls = fileNames
    .filter((n) => n.toLowerCase().endsWith('.html'))
    .sort((a, b) => a.localeCompare(b));
  return htmls[0] ?? null;
}
